<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { type NForm } from 'naive-ui';
import { IconAddCircle, IconCheckCircle, IconDocumentText } from '@/components/design/icons';
import {
  type ContractManagementContractInDb,
  type ContractManagementContractFileInDb,
  type ContractManagementContractCreate,
} from '@/api';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import ContractSelectTable from '@/components/contractManagement/ContractSelectTable.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  show: boolean;
  contractFileUid: string;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (
    e: 'moved',
    contractFileUid: string,
    updatedContractFile: ContractManagementContractFileInDb,
    targetContract?: ContractManagementContractInDb
  ): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const message = useGaiaToast();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

type MoveMode = 'existing' | 'new';
const moveMode = ref<MoveMode>('existing');

// --- Existing contract: use shared ContractSelectTable ---
const selectedContract = ref<ContractManagementContractInDb | null>(null);

// --- New contract: created next to the file's current contract ---
const newContractFormRef = ref<InstanceType<typeof NForm> | null>(null);
const tagsInput = ref('');
const newContractData = ref<ContractManagementContractCreate>({
  name: '',
  contract_type_uid: null,
  tags: [],
  description: '',
});

// Leaving the type empty lets the classifier pick one for the new contract.
const contractTypeStore = useContractTypeStore();

const contractFormRules = {
  name: {
    required: true,
    message: 'Please enter contract name',
    trigger: ['blur', 'input'],
  },
};

const isExistingValid = computed(() => Boolean(selectedContract.value));
const isNewValid = computed(() => {
  const name = newContractData.value.name.trim();
  return Boolean(name);
});

const canSubmit = computed(() =>
  moveMode.value === 'existing' ? isExistingValid.value : isNewValid.value
);

const submitting = ref(false);

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return;

  if (moveMode.value === 'existing' && selectedContract.value) {
    try {
      submitting.value = true;
      const updatedFile = await ContractFilesService.move(props.contractFileUid, {
        target_contract_uid: selectedContract.value.uid,
      });
      message.success('Contract file moved to the selected contract');
      emit('moved', props.contractFileUid, updatedFile, selectedContract.value);
      showModal.value = false;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to move contract file';
      message.error(msg);
    } finally {
      submitting.value = false;
    }
    return;
  }

  if (moveMode.value === 'new') {
    try {
      await newContractFormRef.value?.validate();
    } catch {
      message.error('Please fill in all required contract fields');
      return;
    }

    const tags = tagsInput.value
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    const newContract: ContractManagementContractCreate = {
      name: newContractData.value.name.trim(),
      contract_type_uid: newContractData.value.contract_type_uid,
      tags,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      description: newContractData.value.description?.trim() || undefined,
    };

    try {
      submitting.value = true;
      const updatedFile = await ContractFilesService.move(props.contractFileUid, {
        new_contract: newContract,
      });
      message.success('Contract file moved to a new contract');
      // Pass contract for File Information display: API response or a display-only object with the new contract name
      const contractFromResponse = (updatedFile as { contract?: ContractManagementContractInDb })
        .contract;
      const displayContract: ContractManagementContractInDb = contractFromResponse ?? {
        uid: '',
        name: newContractData.value.name.trim(),
        contract_type: contractTypeStore.contractTypes.find(
          type => type.uid === newContractData.value.contract_type_uid
        ),
        tags,
        description: newContractData.value.description?.trim() ?? null,
        matters: [],
        // The new contract holds just the moved file.
        contract_file_count: 1,
        created_at: new Date().toISOString(),
      };
      emit('moved', props.contractFileUid, updatedFile, displayContract);
      showModal.value = false;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to move contract file';
      message.error(msg);
    } finally {
      submitting.value = false;
    }
  }
}

function handleCancel(): void {
  showModal.value = false;
}

function resetModal(): void {
  moveMode.value = 'existing';
  selectedContract.value = null;
  tagsInput.value = '';
  newContractData.value = {
    name: '',
    contract_type_uid: null,
    tags: [],
    description: '',
  };
  newContractFormRef.value?.restoreValidation();
}

watch(
  showModal,
  newValue => {
    if (newValue) {
      resetModal();
      void contractTypeStore.ensureLoaded();
    }
  },
  { immediate: true }
);
</script>

<template>
  <NModal
    v-model:show="showModal"
    preset="card"
    title="Move Contract File"
    style="width: 900px"
    :mask-closable="false"
  >
    <div class="modal-content">
      <NTabs v-model:value="moveMode" type="line" size="medium">
        <NTabPane name="existing" tab="Move to existing contract">
          <template #tab>
            <span class="tab-label">
              <NIcon size="18" style="margin-right: 6px; vertical-align: middle">
                <IconDocumentText />
              </NIcon>
              Move to existing contract
            </span>
          </template>
          <div class="tab-pane-content">
            <ContractSelectTable v-model:selected="selectedContract" :active="showModal" />
          </div>
        </NTabPane>
        <NTabPane name="new" tab="Move to new contract">
          <template #tab>
            <span class="tab-label">
              <NIcon size="18" style="margin-right: 6px; vertical-align: middle">
                <IconAddCircle />
              </NIcon>
              Move to new contract
            </span>
          </template>
          <div class="tab-pane-content">
            <NForm
              ref="newContractFormRef"
              :model="newContractData"
              :rules="contractFormRules"
              label-placement="top"
            >
              <NFormItem label="Contract name" path="name" required>
                <gaia-input
                  v-model:value="newContractData.name"
                  placeholder="Enter contract name"
                  :maxlength="200"
                />
              </NFormItem>
              <NFormItem label="Contract type">
                <gaia-select
                  v-model:value="newContractData.contract_type_uid"
                  :options="contractTypeStore.uidOptions"
                  placeholder="Detect automatically"
                  clearable
                  filterable
                />
              </NFormItem>
              <NFormItem label="Tags">
                <gaia-input
                  v-model:value="tagsInput"
                  placeholder="Enter tags separated by commas"
                />
              </NFormItem>
              <NFormItem label="Description">
                <gaia-textarea
                  v-model:value="newContractData.description"
                  :rows="3"
                  placeholder="Enter contract description (optional)"
                  :autosize="false"
                />
              </NFormItem>
            </NForm>
          </div>
        </NTabPane>
      </NTabs>
    </div>

    <template #footer>
      <NSpace justify="end">
        <NButton @click="handleCancel">Cancel</NButton>
        <NButton type="primary" :disabled="!canSubmit" :loading="submitting" @click="handleSubmit">
          <template #icon>
            <NIcon><IconCheckCircle /></NIcon>
          </template>
          Move file
        </NButton>
      </NSpace>
    </template>
  </NModal>
</template>

<style scoped>
.modal-content {
  min-height: 380px;
}
.tab-pane-content {
  padding: 8px 0;
  min-height: 320px;
}
.tab-label {
  display: inline-flex;
  align-items: center;
}
</style>
